import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { createArticle } from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  ARTICLE_TYPES,
  ARTICLE_SUBTYPES,
  getTypeLabel,
  getSubtypeLabel
} from '../constants/articleTypes';

const ARTICLE_TYPE_OPTIONS = Object.entries(ARTICLE_TYPES).map(([value, { label }]) => ({
  value,
  label
}));

const ARTICLE_SUBTYPE_OPTIONS = {
  long_social_post: Object.entries(ARTICLE_SUBTYPES)
    .filter(([key]) => key.includes('linkedin_article') || key.includes('facebook_long') || key.includes('medium_story') || key.includes('x_thread'))
    .map(([value, { label }]) => ({
      value,
      label
    }))
};

const CreateArticleDialog = ({ open, onClose, onArticleCreated, projectId }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('article');
  const [subtype, setSubtype] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setType(newType);
    // Reset subtype when type changes
    setSubtype('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const newArticle = await createArticle({ 
        title, 
        topic: title,  // Use the same value for topic
        project_id: projectId,
        type,
        subtype: subtype || null
      });
      onArticleCreated(newArticle);
      handleClose();
    } catch (error) {
      console.error('Error creating article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setType('article');
    onClose();
  };

  const handleIdeasClick = () => {
    handleClose();
    navigate('/ideas');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Create New Article
          {projectId && <Typography variant="subtitle2" color="textSecondary">
            In current project
          </Typography>}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={type} onChange={handleTypeChange} label="Type">
                {ARTICLE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {type === 'long_social_post' && (
              <FormControl fullWidth>
                <InputLabel>Subtype</InputLabel>
                <Select 
                  value={subtype} 
                  onChange={(e) => setSubtype(e.target.value)}
                  label="Subtype"
                >
                  {ARTICLE_SUBTYPE_OPTIONS.long_social_post.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Typography variant="body2" color="text.secondary">
              Need ideas? <Link onClick={handleIdeasClick} sx={{ cursor: 'pointer' }}>Generate some</Link>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!title.trim() || loading}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateArticleDialog; 